import { IHttpMethod } from '../types';
import { auth } from './auth';

declare var __API_SERVER_URL__;
const BACKEND_URL = (typeof __API_SERVER_URL__ === 'undefined' ? 'http://localhost:9966/petclinic' : __API_SERVER_URL__);

export const url = (path: string): string => {
  // Remove leading slash from path if present to avoid double slashes
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  return `${BACKEND_URL}/${cleanPath}`;
};

/**
 * Create fetch options with authentication headers
 */
export const createFetchOptions = (options: RequestInit = {}): RequestInit => {
  const headers: any = {
    'Accept': 'application/json',
    ...(options.headers || {})
  };

  // Add Basic Auth header if user is authenticated
  const authHeader = auth.getBasicAuthHeader();
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }

  return {
    ...options,
    headers: headers
    // Note: Not using credentials: 'include' because we're using Basic Auth via Authorization header
  };
};

/**
 * Safely parse JSON from a Response object, handling empty responses and errors
 */
export const safeJsonParse = (response: Response): Promise<any> => {
  // Handle 204 No Content
  if (response.status === 204) {
    return Promise.resolve({});
  }
  
  // Check content type
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return Promise.resolve({ message: `HTTP ${response.status}: ${response.statusText}` });
  }
  
  // Try to parse JSON, but handle empty responses
  return response.text().then(text => {
    if (!text || text.trim() === '') {
      return { message: `HTTP ${response.status}: ${response.statusText}` };
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      console.error('JSON parse error:', e, 'Response text:', text);
      return { message: text || `HTTP ${response.status}: ${response.statusText}`, error: 'Invalid JSON response' };
    }
  });
};

/**
 * path: relative PATH without host and port (i.e. '/api/123')
 * data: object that will be passed as request body
 * onSuccess: callback handler if request succeeded. Succeeded means it could technically be handled (i.e. valid json is returned)
 * regardless of the HTTP status code.
 */
export const submitForm = (method: IHttpMethod, path: string, data: any, onSuccess: (status: number, response: any) => void) => {
  const requestUrl = url(path);

  // Build headers with Basic Auth if available
  const headers: any = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };

  // Add Basic Auth header if user is authenticated
  const authHeader = auth.getBasicAuthHeader();
  if (authHeader) {
    headers['Authorization'] = authHeader;
    console.log('Adding Authorization header for authenticated user');
  } else {
    console.warn('No Authorization header - user may not be logged in');
  }

  const fetchParams = {
    method: method,
    headers: headers,
    body: JSON.stringify(data)
    // Note: Not using credentials: 'include' because we're using Basic Auth via Authorization header
  };

  console.log('Submitting to ' + method + ' ' + requestUrl);
  return fetch(requestUrl, fetchParams)
    .then(response => {
      // Handle 204 No Content - but try to parse body in case backend sends data
      if (response.status === 204) {
        // Check if there's a response body to parse
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          // Backend might send JSON even with 204, try to parse it
          return response.text().then(text => {
            if (text && text.trim() !== '') {
              try {
                const result = JSON.parse(text);
                return onSuccess(response.status, result);
              } catch (e) {
                // If parsing fails, return empty object
                return onSuccess(response.status, {});
              }
            }
            return onSuccess(response.status, {});
          });
        }
        // No content type or not JSON, return empty object
        return onSuccess(response.status, {});
      }
      // Handle empty responses (like 401 with no body)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return onSuccess(response.status, { message: `HTTP ${response.status}: ${response.statusText}` });
      }
      // Try to parse JSON, but handle empty responses
      return response.text().then(text => {
        if (!text || text.trim() === '') {
          return onSuccess(response.status, { message: `HTTP ${response.status}: ${response.statusText}` });
        }
        try {
          const result = JSON.parse(text);
          return onSuccess(response.status, result);
        } catch (e) {
          return onSuccess(response.status, { message: text || `HTTP ${response.status}: ${response.statusText}` });
        }
      });
    })
    .catch(error => {
      console.error('Network error:', error);
      // Handle network errors (CORS, connection refused, etc.)
      onSuccess(0, { message: 'Network error: Unable to connect to server. Please ensure the backend is running.' });
    });
};
